import { join } from 'node:path';
import { globby } from 'globby';
import { builders, generateCode, loadFile, parseModule } from 'magicast';
import { getDefaultExportOptions } from 'magicast/helpers';
import type { ASTNode, ProxifiedModule } from 'magicast';
import { outputFile } from '@wix/cli-fs';
import { CliError, CliErrorCode } from '@wix/cli-error';

export async function modifyAstroConfig(projectFolder: string) {
  const configFilesFound = await globby('astro.config.{js,cjs,mjs,ts}', {
    cwd: projectFolder,
    onlyFiles: true,
  });

  if (!hasOnlyOneValue(configFilesFound)) {
    throw new CliError({
      code: CliErrorCode.FailedToFindAstroConfig(),
      cause: undefined,
    });
  }

  const file = join(projectFolder, configFilesFound[0]);
  const loadedFile = await loadFile(file, {});

  const react = setImport(loadedFile, '@astrojs/react', 'react');
  addIntegration(loadedFile, react);

  const wix = setImport(loadedFile, '@wix/astro', 'wix');
  addIntegration(loadedFile, wix);

  const adapterName = setImport(
    loadedFile,
    '@wix/cloud-provider-fetch-adapter',
    'cloudProviderFetchAdapter'
  );
  addIsBuildVariable(loadedFile);
  setConditionalAdapter(loadedFile, adapterName);

  const config = getDefineConfigOptions(loadedFile);
  config.image ??= {};
  config.image.domains ??= [];

  if (!config.image.domains.includes('static.wixstatic.com')) {
    config.image.domains.push('static.wixstatic.com');
  }

  if (!config.output) {
    config.output = 'server';
  }

  await outputFile(file, generateCode(loadedFile).code);
}

function hasOnlyOneValue(values: string[]): values is [string] {
  return values.length === 1;
}

function setImport(target: ProxifiedModule, from: string, local: string) {
  const imports = target.imports;
  const existingImport = imports.$items.find((item) => item.from === from);

  if (existingImport) {
    return existingImport.local;
  }

  imports.$append({ imported: 'default', local, from });
  return local;
}

function getModuleBody(target: ProxifiedModule): ASTNode[] | undefined {
  const ast = target.$ast as {
    program?: { body?: ASTNode[] };
    body?: ASTNode[];
  };
  // Handle both File node (with program.body) and direct Program node (with body)
  return ast.program?.body ?? ast.body;
}

function addIsBuildVariable(target: ProxifiedModule) {
  const body = getModuleBody(target);
  if (!body) {
    return;
  }

  // Check if isBuild already exists
  const hasIsBuild = body.some(
    (node) =>
      node.type === 'VariableDeclaration' &&
      node.declarations.some(
        (decl) => decl.id.type === 'Identifier' && decl.id.name === 'isBuild'
      )
  );

  if (hasIsBuild) {
    return;
  }

  // Parse the variable declaration using magicast
  const snippet = parseModule(
    `const isBuild = process.env.NODE_ENV == "production";`
  );
  const snippetBody = getModuleBody(snippet);
  const isBuildDeclaration = snippetBody?.[0];

  if (!isBuildDeclaration) {
    return;
  }

  // Find the export default index and insert before it
  const exportIndex = body.findIndex(
    (node) => node.type === 'ExportDefaultDeclaration'
  );

  if (exportIndex !== -1) {
    body.splice(exportIndex, 0, isBuildDeclaration);
  }
}

function setConditionalAdapter(target: ProxifiedModule, adapterName: string) {
  const config = getDefineConfigOptions(target);

  // Check if adapter already exists as a direct property
  if (config.adapter) {
    return;
  }

  const configAst = config.$ast as {
    properties?: Array<{
      type: string;
      key?: { name?: string };
      argument?: {
        left?: { properties?: Array<{ key?: { name?: string } }> };
        right?: { properties?: Array<{ key?: { name?: string } }> };
      };
    }>;
  };

  // Check if adapter property exists (direct or in spread)
  const hasAdapter = configAst.properties?.some(
    (prop) =>
      // Direct property: adapter: ...
      prop.key?.name === 'adapter' ||
      // Spread with adapter: ...(isBuild && { adapter: ... })
      (prop.type === 'SpreadElement' &&
        (prop.argument?.right?.properties?.some(
          (p) => p.key?.name === 'adapter'
        ) ??
          prop.argument?.left?.properties?.some(
            (p) => p.key?.name === 'adapter'
          )))
  );

  if (hasAdapter) {
    return;
  }

  // Parse the spread argument using magicast's builders.raw
  // Creates: ...(isBuild && { adapter: adapterName({}) })
  const spreadArgument = builders.raw(
    `isBuild && { adapter: ${adapterName}({}) }`
  );

  const spreadElement = {
    type: 'SpreadElement',
    argument: spreadArgument.$ast,
  };

  configAst.properties?.push(spreadElement);
}

function addIntegration(target: ProxifiedModule, name: string) {
  const config = getDefineConfigOptions(target);

  config.integrations ??= [];
  if (
    !config.integrations.$ast.elements.some(
      (el: ASTNode) =>
        el.type === 'CallExpression' &&
        el.callee.type === 'Identifier' &&
        el.callee.name === name
    )
  ) {
    config.integrations.push(builders.functionCall(name));
  }
}

// returns {} from `export default defineConfig({});`
function getDefineConfigOptions(target: ProxifiedModule) {
  return getDefaultExportOptions(target);
}
